import { applyLoanToPolicy, approvePolicy, archivePolicy, cancelPolicyRequest, changePolicyReviewState, createPolicyFromQuote, deleteMotorPolicy, deleteSingleNonMotorPolicy, downloadPolicies, downloadPolicyDetails, generatePolicyAgreementDocuments, generateSaleForAPolicy, getAllBrokerPoliciesCount, getInsurerListOfExpiringPolicies, getListOfExpiringPolicies, getNonMotorPolicyByPolicyNumber, getPaymentLink, getPolicy, initializeHubtelPayment, initializeItConsortiumPayment, makeBulkPaymentForPolicy, reloadPolicyVehiclePhotos, requestPolicyVehiclePhotos, runPaymentAnalysisOnPolicy, selfApprovePolicy, updatePolicy, verifyVehicle } from '../api/requests';
import { MOBILE_MONEY_NETWORKS, POLICY_REVIEW_STATES, UPLOAD_IMAGE_ENDPOINTS } from '../resources/enums';
import { IPolicy } from '../models/policy/IPolicy';
import { Policy } from '../models/policy/Policy';
import { formatCurrencyAmount, getFormattedDate, getFormattedPaymentChannel, getInstallmentTypeFromKey, getPhoneNumberFromShortCodeFormat, policyFormDataToUpdatePolicyDTO, removeUnderscoresAndDashes, showToast } from '../utils/miscFunctions';
import { IInitiateHubtelPaymentDTO, IPolicyDocument, IReviewPolicyDTO, ITask, IVerifyMobilePaymentDTO } from '../resources/interfaces';
import { ISelectDropdownItem } from '../components/form-control/SelectDropdown';
import useModal from './modalHook';
import QuoteIframe from '../components/dashboard/quoteWidget/QuoteWidget';
import AssignPolicyToCommunity from '../views/admin/communities/AssignPolicyToCommunity';
import ReviewPolicy from '../views/shared/policies/auto/ReviewPolicy';
import RejectToDraft from '../views/shared/policies/auto/RejectToDraft';
import { MdDateRange } from 'react-icons/md';
import {
  FaCar, FaHome, FaPlane, FaHeartbeat, FaBriefcase, FaShip,
  FaFire, FaUserShield, FaGavel, FaUsers,
  FaUserInjured,
  FaShieldAlt,
  FaBox, FaMoneyBillWave, FaGasPump,
  FaCog,
  FaFileContract,
  FaUserFriends,
  FaLink
} from 'react-icons/fa';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import FleetModal from '../components/modals/FleetModal';
import QuoteCalculator from '../views/admin/quotes/calculators/widgetChecker';
import PolicyApproval from '../views/shared/policies/auto/PolicyApproval';
import { FleetPolicy } from '../models/policy/FleetPolicy';
import { Ban, CheckCircle, Download, FileSearch, Pause, PauseIcon, XIcon } from 'lucide-react';
import SubmitForApproval from '../views/shared/policies/auto/SubmitForApproval';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useRoute from '../hooks/routesHook';
import useUserAccount from './userAccountHook';
import UploadDocumentFromDetailsPageModal from '../components/modals/UploadDocumentFromDetailsPageModal';
import { PolicySummary } from '../models/policy/PolicySummary';
import AssignPolicyManager from '../views/shared/policies/auto/AssignPolicy';
import PaymentLinkModal from '../views/shared/policies/auto/paymentLinkModal';
import NonAutoPolicy from '../models/nonAutoPolicy/NonAutoPolicy';
import PaymentEditModal from '../views/shared/payments/PaymentEditModal';
import ApprovePaymentModal from '../views/shared/payments/PaymentApproval';
import { IPayment } from '../models/payment/IPayment';
import { LuExternalLink } from 'react-icons/lu';
import PaymentStatus from '../components/PaymentStatus';
import { CLEAR_QUOTE_WIDGET_PAYLOAD } from '../states/slices/quoteWidgetSlice';
import { ITableAction } from '../components/elements/GTable/GTable';
import CompleteNonAutoPolicyPayment from '../views/shared/policies/non-auto/components/CompleteNonAutoPolicyPayment';
import MarkPolicyAsRenewed from '../views/shared/policies/auto/MarkPolicyAsRenewed';
import ActionConfirmation from '../components/elements/ActionConfirmation';
import { generatePaymentReceipt } from '../services/policies';
import Button from '../components/form-control/Button';
import AssignNonMotorPolicy from '../views/shared/policies/non-auto/components/AssignNonMotorPolicy';
import CopyLink from '../components/CopyLink';
import ShareDocumentModal from '../views/shared/policies/auto/ShareDocumentModal';
import MarkPolicyAsDone from '../views/shared/policies/auto/MarkPolicyAsDone';
import CreateTask from '../views/admin/leads/CreateTask';
import ApplyDiscount from '../views/shared/policies/auto/ApplyDiscount';
import { VehicleFormValues, VehicleInformationForm } from '../views/shared/policies/auto/PolicyInformationEditModal';
import PreviewImage from '../components/elements/PreviewImage';
import RequestReviewModal from '../views/shared/policies/auto/RequestReviewModal';
import { IPolicyRenewal } from '../views/shared/policies/auto/Renewals';
import { IFormData } from '../components/downloadComponents/DownloadAllPolicies';
import moment from 'moment';
import { INPUT_DATE_FORMAT } from '../resources/constants';
import VerifyPaymentOTP from '../views/shared/payments/VerifyPaymentOTP';
import ProvideMomoDetails from '../views/shared/payments/ProvideMomoDetails';
import { useMemo, useRef } from 'react';
import PolicyStatus from '../components/elements/PolicyStatus';
import { TbClipboardCopy } from 'react-icons/tb';
import IconAction from '../components/elements/GTable/InGTableComponents/IconAction';
import { getSelectedCountry } from '../states/slices/resourceSlice';
import GenerateStickerModal from '../views/shared/policies/auto/GenerateStickerModal';


interface PolicyTypesBadgeProps {
  policyType?: string;
  showAll?: boolean;
  onSelect?: (policyType: any) => void;
}

function usePolicy() {
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const [searchParam] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const PolicyModalComponent = ModalComponent;
  const queryClient = useQueryClient();
  // Tracks payments whose offline-payment modal is open or whose post-submit
  // refetch is still in flight. Used by showEditPaymentModal to refuse a
  // second open for the same payment id while the first is still settling —
  // the dropdown entry stays visible for a few hundred ms after success
  // (until the policy refetch lands), and impatient users were clicking it
  // again, getting the backend's idempotent "already processed" error as a
  // scary red toast. The ref persists across re-renders without triggering
  // them; a Set lets us key the guard per payment id.
  const offlinePaymentInFlight = useRef<Set<string | number>>(new Set());
  const params = useParams();
  const policyNumber = params?.policyId;
  const policyType = searchParam.get('type');
  const isMotor = policyType !== undefined && policyType === 'motor'
  const { initializeHubtel } = useHubtelPayment();
  const { user } = useUserAccount();
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1

  const clearPolicyJourneyWizardData = (isRenewal?: string) => {
    try {
      const suffix = isRenewal === 'true' ? '_renewal' : '';
      localStorage.removeItem(`policyWizardData${suffix}`);
      localStorage.removeItem(`policyWizardStep${suffix}`);

    } catch (error) {
      console.error('Error clearing form data from localStorage:', error);
    }
  };

  const fetchPolicy = async (policyId: string) => {
    try {
      if (policyId) {
        const response = await getPolicy(policyId);
        return (new Policy(response.data));
      }
    } catch (error) {
      // showToast.error('Failed to fetch policy details. Please try again later.');
    }
  };

  const requestPolicy = (policyRef: string): Promise<Policy> => {
    return new Promise<Policy>((resolve, reject) => {
      getPolicy(policyRef)
        .then((response: any) => {
          resolve(new Policy(response));
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const getAllPolicies = (): Promise<Policy[]> => {
    return new Promise<Policy[]>((resolve, reject) => {
      getAllPolicies()
        .then((response: any) => {
          const policies: Policy[] = [];
          if (response) {
            response?.forEach((policy: IPolicy) => {
              policies.push(new Policy(policy));
            });
          }
          resolve(policies);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const approveSinglePolicy = (policyRef: string): Promise<Policy> => {
    return new Promise<Policy>((resolve, reject) => {
      approvePolicy(policyRef)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  // Self Approval — broker-initiated, skips the insurer review queue.
  // Backend gates on Intermediary.issuanceMode (MID_LIVE / INSURER_API only);
  // see selfApprovePolicy in api/requests.ts for the endpoint contract.
  const selfApproveSinglePolicy = (policyRef: string): Promise<Policy> => {
    return new Promise<Policy>((resolve, reject) => {
      selfApprovePolicy(policyRef)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const reviewPolicy = (policyRef: string, payload: IReviewPolicyDTO): Promise<any> => {
    return new Promise<any>((resolve, reject) => {
      changePolicyReviewState(policyRef, payload)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const cancelPolicy = (policyRef: string, reason: string, terminate: boolean): Promise<Policy> => {
    return new Promise<Policy>((resolve, reject) => {
      cancelPolicyRequest(policyRef, reason, terminate)
        .then((response: any) => {
          resolve(new Policy(response));
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const updatePolicyDetails = (policyId: string, data: any): Promise<void> => {
    const payload = policyFormDataToUpdatePolicyDTO(data);

    return new Promise<void>((resolve, reject) => {
      updatePolicy(policyId, payload)
        .then((response: any) => {
          resolve(response);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const generatePolicyFromQuote = (quoteId: string): Promise<Policy> => {
    return new Promise<Policy>((resolve, reject) => {
      createPolicyFromQuote(quoteId)
        .then((response: any) => {
          resolve(new Policy(response?.data));
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const POLICY_FILTERS: ISelectDropdownItem[] = [
    {
      name: 'All',
      value: 'All'
    },
    {
      name: 'Pending Approval',
      value: POLICY_REVIEW_STATES.PENDING_APPROVAL
    },
    {
      name: 'In Approval',
      value: POLICY_REVIEW_STATES.IN_APPROVAL
    },
    {
      name: 'Active',
      // name: 'Approved',
      value: POLICY_REVIEW_STATES.APPROVED
    },
    {
      name: 'In Review',
      value: POLICY_REVIEW_STATES.IN_REVIEW
    },
    {
      name: 'On Hold',
      value: POLICY_REVIEW_STATES.ON_HOLD
    },
    // No 'New' filter: there is no "New" status in the policy-status spec. A
    // freshly created (reviewStage=NEW) policy displays as "Draft" (unpaid) or
    // "Pending Approval" (paid), so the Draft/Pending Approval filters already
    // cover it. Exposing a "New" filter just showed "Draft"-badged rows.
    {
      name: 'Draft',
      value: POLICY_REVIEW_STATES.DRAFT
    },
    // Bulk-imported "Existing" book — its own tab so it no longer shows up
    // under Draft (both are NEW-stage, but Existing is not an unsubmitted draft).
    {
      name: 'Existing',
      value: POLICY_REVIEW_STATES.EXISTING
    },
    {
      name: 'Expired',
      value: POLICY_REVIEW_STATES.EXPIRED
    },
    {
      name: 'Cancelled',
      value: POLICY_REVIEW_STATES.CANCELLED
    },
    {
      name: 'Declined',
      value: POLICY_REVIEW_STATES.DECLINED
    },
    {
      name: 'Archived',
      value: POLICY_REVIEW_STATES.ARCHIVED
    },
    {
      name: 'Due Later',
      value: POLICY_REVIEW_STATES.DUE_LATER
    }
  ];

  const INSURER_POLICY_FILTERS: ISelectDropdownItem[] = [
    {
      name: 'All',
      value: 'All'
    },
    {
      name: 'Approved',
      value: POLICY_REVIEW_STATES.APPROVED
    },
    {
      name: 'Declined',
      value: POLICY_REVIEW_STATES.DECLINED
    },
    {
      name: 'Cancelled',
      value: POLICY_REVIEW_STATES.CANCELLED
    },
    {
      name: 'Expired',
      value: POLICY_REVIEW_STATES.EXPIRED
    }
  ];

  const renewMotorPolicy = (policy: Policy | PolicySummary | IPolicyRenewal) => {

    try {
      const policyRef = policy instanceof Policy ? policy.ref : policy.policyNumber;
      clearPolicyJourneyWizardData("true")
      navigate(`${getDashboardBase()}/policies/dynamic-create-policy?type=motor&policyNumber=${policyRef}&isRenewal=true`);

    } catch (error) {
      console.error('Error renewing motor policy:', error);
    }

  };

  type IMarkedAsRenewedProps = {
    customerName: string,
    policyNumber: string, policyType:
    string,
    vehicleMake?: string,
    vehicleModel?: string,
    onProceed?: () => void
  }

  const markAsRenewed = async ({ customerName, policyNumber, vehicleMake, vehicleModel, policyType, onProceed }: IMarkedAsRenewedProps) => {
    updateModalStates({
      show: true,
      static: true,
      size: '3xl',
      title: 'Mark as Renewed',
      children: (
        <MarkPolicyAsRenewed
          vehicleMake={vehicleMake}
          vehicleModel={vehicleModel}
          customerName={customerName}
          policyNumber={policyNumber}
          policyType={policyType}
          onclose={closeModal}
          onProceed={onProceed} />
      ),
    });
  };

  const addQuote = (signupData?: string, isRenewal?: boolean, policyNumber?: string) => {
    updateModalStates({
      show: true,
      size: 'lg',
      children: (
        <QuoteIframe
          onProceed={() => {
            closeModal();
          }}
          onClose={closeModal}
          signup={signupData}
          isRenewPolicy={isRenewal}
          policyNumber={policyNumber}
        />
      ),
      classes: '!p-0 r-[5px] !shadow-none !bg-none',
      static: true,
      hidePanelStyles: true,
      isHeadRounded: false
    });
  };

  const assignPoliciesToCommunity = (selectedPolicies: []) => {
    updateModalStates({
      show: true,
      children: (
        <AssignPolicyToCommunity
          onProceed={closeModal}
          onClose={closeModal}
          policyNumbers={selectedPolicies}
        />
      ),
      title: 'Assign Policies To Community',
      classes: '!p-0 r-[5px]',
      size: 'md'
    });
  };

  const setPolicyToDueLater = (policy: Policy) => {
    updateModalStates({
      show: true,
      children: (
        <ReviewPolicy
          policy={policy}
          onProceed={() => {
            closeModal();
          }}
          onCancel={closeModal}
          title="Set to due later"
          titleIcon={<MdDateRange />}
          type={POLICY_REVIEW_STATES.DUE_LATER}
        />
      ),
      size: 'lg'
    });
  };

  const handleReloadPolicyVehiclePhotos = async (policy: Policy) => {
    try {
      const response = await reloadPolicyVehiclePhotos(policy?.ref);
      if (response) {
        showToast.success(response?.message ?? 'Policy vehicle photos reloaded successfully',);
        queryClient.invalidateQueries({ queryKey: ['policy', policy?.ref] });
      }
    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to reload policy vehicle photos',);
    }
  }

  const uploadFleet = () => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <FleetModal
          onProceed={(file: File) => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getPayoutAsAnInsurer'] });
            queryClient.invalidateQueries({ queryKey: ['getTeamPayouts'] });
          }}
          onClose={closeModal}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  const uploadPolicyDocument = (policyNumnber: string, queryKey: string, documentList: any[], url: UPLOAD_IMAGE_ENDPOINTS, policyType: 'auto' | 'nonAuto', policyDocuments: IPolicyDocument[]) => {
    updateModalStates({
      show: true,
      size: 'sm',
      showTitleSection: true,
      children: (
        <UploadDocumentFromDetailsPageModal
          policyId={policyNumnber || ''}
          onClose={closeModal}
          onProceed={closeModal}
          documentList={documentList || []}
          url={url}
          policyType={policyType}
          policyDocuments={policyDocuments || []}
          queryKey={queryKey}
        />
      ),
      title: 'Upload Document',
      classes: '!p-0 r-[5px]'
    });
  };

  const handleApplyLoanToPolicy = async (policy: Policy, onProceed: () => void) => {
    if (!policy?.ref) return
    try {
      const response: any = await applyLoanToPolicy(policy?.ref);
      if (response) {
        showToast.success(response?.message ?? 'Premium Financing applied successfully',);
        onProceed();
      }
    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to apply loan',);
    }
  };

  const POLICY_TYPES = [
    {
      key: 'VEHICLE',
      title: 'Motor Vehicle Insurance',
      description: 'Insurance policy for automobiles, motorcycles, trucks and other vehicles',
      icon: FaCar,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      key: 'HOME',
      title: 'Home Insurance',
      description: 'Insurance policy for residential properties and their contents',
      icon: FaHome,
      color: 'bg-purple-100 text-purple-800 border-purple-200'
    },
    {
      key: 'TRAVEL',
      title: 'Travel Insurance',
      description: 'Insurance policy covering travel-related risks and emergencies',
      icon: FaPlane,
      color: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    },
    {
      key: 'HEALTH',
      title: 'Health Insurance',
      description: 'Insurance policy for medical and health-related expenses',
      icon: FaHeartbeat,
      color: 'bg-pink-100 text-pink-800 border-pink-200'
    },
    {
      key: 'LIFE',
      title: 'Life Insurance',
      description: 'Insurance policy providing life coverage and benefits',
      icon: FaUserShield,
      color: 'bg-green-100 text-green-800 border-green-200'
    },
    {
      key: 'BUSINESS',
      title: 'Business Insurance',
      description: 'Insurance policy for business operations and commercial risks',
      icon: FaBriefcase,
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    },
    {
      key: 'MARINE',
      title: 'Marine Insurance',
      description: 'Insurance policy for maritime vessels and cargo transportation',
      icon: FaShip,
      color: 'bg-cyan-100 text-cyan-800 border-cyan-200'
    },
    {
      key: 'FIRE',
      title: 'Fire and Allied Perils Insurance',
      description: 'Insurance policy covering fire damage and related perils',
      icon: FaFire,
      color: 'bg-orange-100 text-orange-800 border-orange-200'
    },
    {
      key: 'PERSONAL_ACCIDENT',
      title: 'Personal Accident Insurance',
      description: 'Insurance policy covering personal accidents and injuries',
      icon: FaUserInjured,
      color: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200'
    },
    {
      key: 'PROFESSIONAL_INDEMNITY',
      title: 'Professional Indemnity Insurance',
      description: 'Insurance policy covering professional liability and errors',
      icon: FaGavel,
      color: 'bg-gray-100 text-gray-800 border-gray-200'
    },
    {
      key: 'PUBLIC_LIABILITY',
      title: 'Public Liability Insurance',
      description: 'Insurance policy covering public liability claims',
      icon: FaUsers,
      color: 'bg-teal-100 text-teal-800 border-teal-200'
    },
    {
      key: 'ASSET_ALL_RISK',
      title: 'Asset All Risk',
      description: 'Insurance covering losses from theft or damage to money',
      icon: FaShieldAlt,
      color: 'bg-amber-100 text-amber-800 border-amber-200'
    },
    {
      key: 'GOODS_IN_TRANSIT',
      title: 'Goods In Transit',
      description: 'Insurance covering goods in transit',
      icon: FaBox,
      color: 'bg-lime-100 text-lime-800 border-lime-200'
    },
    {
      key: 'MONEY_INSURANCE',
      title: 'Money Insurance',
      description: 'Insurance covering losses from theft or damage to money',
      icon: FaMoneyBillWave,
      color: 'bg-emerald-100 text-emerald-800 border-emerald-200'
    },
    {
      key: 'FIDELITY_GUARANTEE',
      title: 'Fidelity Guarantee',
      description: 'Insurance covering losses from employee dishonesty',
      icon: FaShieldAlt,
      color: 'bg-violet-100 text-violet-800 border-violet-200'
    },
    {
      key: 'PETROLEUM_BOND',
      title: 'Petroleum Bond',
      description: 'Insurance covering petroleum bond',
      icon: FaGasPump,
      color: 'bg-rose-100 text-rose-800 border-rose-200'
    },
    {
      key: 'GROUP_PERSONAL_ACCIDENT',
      title: 'Group Personal Accident',
      description: 'Insurance covering group personal accident',
      icon: FaGasPump,
      color: 'bg-sky-100 text-sky-800 border-sky-200'
    },
    {
      key: 'PLANT_AND_MACHINERY',
      title: 'Plant and Machinery',
      description: 'Insurance covering plant and machinery',
      icon: FaCog,
      color: 'bg-green-100 text-green-800 border-green-200'
    },

    {
      key: 'CUSTOMS_REMOVAL_BOND',
      title: 'Customs Removal Bond',
      description: 'Insurance covering customs removal bond',
      icon: FaBox,
      color: 'bg-indigo-100 text-indigo-800 border-indigo-200'
    },
    {
      key: 'PERFORMANCE_BOND',
      title: 'Performance Bond',
      description: 'Insurance covering performance bond',
      icon: FaFileContract,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },

    {
      key: 'BID_BOND',
      title: 'Bid Bond',
      description: 'Insurance covering bid bond',
      icon: FaFileContract,
      color: 'bg-purple-100 text-purple-800 border-purple-200'
    },

    {
      key: "WORKMEN'S COMPENSATION POLICY",
      title: "Workmen's Compensation Policy",
      description: "Insurance covering workmen's compensation",
      icon: FaUserInjured,
      color: 'bg-orange-100 text-orange-800 border-orange-200'
    },
    {
      key: "WORKFORCE INCOME SECURITY",
      title: "Workforce Income Security",
      description: "Insurance covering workforce income security",
      icon: FaUserFriends,
      color: 'bg-teal-100 text-teal-800 border-teal-200'
    },
    {
      key: "BANKERS INDEMNITY",
      title: "Bankers Indemnity",
      description: "Insurance covering bankers indemnity",
      icon: FaShieldAlt,
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    },
    {
      key: "CYBER LIABILITY",
      title: "Cyber Liability",
      description: "Insurance covering cyber liability",
      icon: FaShieldAlt,
      color: 'bg-red-100 text-red-800 border-red-200'
    },
    {
      key: "EMPLOYER LIABILITY",
      title: "Employer Liability",
      description: "Insurance covering employer liability",
      icon: FaUserFriends,
      color: 'bg-amber-100 text-amber-800 border-amber-200'
    },
    {
      key: "EQUIPMENT INSURANCE",
      title: "Equipment Insurance",
      description: "Insurance covering equipment insurance",
      icon: FaCog,
      color: 'bg-gray-100 text-gray-800 border-gray-200'
    },
    {
      key: "ELECTRONIC EQUIPMENT",
      title: "Electronic Equipment",
      description: "Insurance covering electronic equipment",
      icon: FaCog,
      color: 'bg-gray-100 text-gray-800 border-gray-200'
    },
    {
      key: "CUSTOMS REMOVAL BOND",
      title: "Customs Removal Bond",
      description: "Insurance covering customs removal bond",
      icon: FaBox,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      key: "PERFORMANCE BOND",
      title: "Performance Bond",
      description: "Insurance covering performance bond",
      icon: FaBox,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      key: "BID BOND",
      title: "Bid Bond",
      description: "Insurance covering bid bond",
      icon: FaBox,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      key: "FIRE & BURGLARY",
      title: "Fire & Burglary",
      description: "Insurance covering fire and burglary",
      icon: FaBox,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      key: "CONTRACTORS ALL RISK",
      title: "Contractors All Risk",
      description: "Insurance covering contractors all risk",
      icon: FaBox,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      key: "EMPLOYER_LIABILITY",
      title: "Employer Liability",
      description: "Insurance covering employer legal liability to employees",
      icon: FaUserInjured,
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    }
  ];

  const PolicyTypesBadge = ({ policyType, showAll = false, onSelect }: PolicyTypesBadgeProps) => {
    if (showAll) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {POLICY_TYPES.map((type) => {
            const IconComponent = type.icon;
            return (
              <div
                key={type.key}
                onClick={() => onSelect?.(type)}
                className={`
                flex items-start gap-3 p-4 rounded-lg border cursor-pointer
                transition-all duration-200 hover:shadow-md hover:scale-[1.02]
                ${type.color}
                ${onSelect ? 'hover:opacity-80' : ''}
              `}
              >
                <div className="flex-shrink-0 mt-1">
                  {type.icon && <type.icon className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm mb-1 truncate">
                    {type.title}
                  </h3>
                  <p className="text-xs opacity-80 line-clamp-2">
                    {type.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (policyType) {
      const selectedType = POLICY_TYPES.find(type => type.key === policyType);
      if (selectedType) {
        const IconComponent = selectedType.icon;
        return (
          <div className={`w-fit
          inline-flex items-center gap-2 px-3 py-1.5 h-[24px] rounded-full border text-sm font-medium
          ${selectedType.color}
        `}>
            {selectedType.icon && <selectedType.icon className="w-4 h-4" />}
            <span className="whitespace-nowrap">{selectedType.title}</span>
          </div>
        );
      }
    }

    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 h-[24px] rounded-full border text-sm font-medium bg-gray-100 text-gray-800 border-gray-200">
        <FaBriefcase className="w-4 h-4" />
        <span className="whitespace-nowrap">Insurance Policy</span>
      </div>
    );
  }

  type IPolicyStatusBadgeProps = {
    status: boolean
  }

  const PolicyStatusBadge = ({ status }: IPolicyStatusBadgeProps) => {
    return (
      <div className="">
        {status ? (
          <div className="px-2 border  border-[#0F7125] text-xs flex  h-[24px] w-fit items-center justify-center text-[#0F7125] rounded-full ">
            <CheckCircle className="inline-block w-4 h-4 mr-1" />Active
          </div>
        ) : (
          <div className=" px-2 border border-red-800 text-xs flex  h-[24px] w-fit items-center justify-center text-red-800 rounded-full ">
            <Ban className="inline-block w-4 h-4 mr-1" /><span>Inactive</span>
          </div>
        )}
      </div>
    )
  }

  const getAllExpiringPolicies = async (page: number, size: number, renewalState: string, insurerId?: string, expiredTo?: string, expiredFrom?: string, month?: number, year?: number, searchParam?: string, brokerId?: string) => {
    try {
      // Insurer users hit a separate endpoint scoped to their own insurer +
      // every broker linked to them; the broker endpoint would either reject
      // them or return nothing. The shared insurerId filter slot is reused as
      // brokerId here (the spec parallel of the broker side's narrow), but
      // the existing Renewals page filter chip is named "Insurer" — until that
      // UI is renamed to "Broker" for insurer role, we pass undefined so the
      // insurer sees the unfiltered cross-broker list.
      const response: any = user?.isInsurer()
        ? await getInsurerListOfExpiringPolicies(page, size, renewalState, undefined, expiredTo, expiredFrom, searchParam)
        : await getListOfExpiringPolicies(page, size, renewalState, insurerId, expiredTo, expiredFrom, month, year, searchParam, brokerId);
      return response.data;

    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to get expiring policies',);
    }
  };

  const handleQuoteCalculator = () => {
    updateModalStates({
      show: true,
      size: '2xl',
      children: (
        <QuoteCalculator />
      ),
      static: true,
    });
  }

  const handleSubmitForReview = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      children: (
        <ReviewPolicy
          policy={policy}
          onProceed={() => {
            closeModal();
            onSuccess();
          }}
          onCancel={closeModal}
          title="Submit for review"
          titleIcon={<Pause />}
          type={POLICY_REVIEW_STATES.IN_REVIEW}
        />
      ),

      classes: '!p-0 r-[5px]',
      size: 'md'
    });
  };

  const handleDeclinePolicy = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      title: 'Decline Policy',
      children: (
        <ReviewPolicy
          policy={policy}
          onProceed={() => {
            closeModal();
            onSuccess();
          }}
          onCancel={closeModal}
          title="Decline Policy"
          titleIcon={<FileSearch />}
          type={POLICY_REVIEW_STATES.DECLINED}
        />
      ),
      classes: '!p-0 r-[5px]',
      size: 'lg'
    });
  };

  // Insurer-only escape hatch: reopen a policy back to Draft so the
  // broker regains full edit rights. Distinct from Decline (which
  // stays in DECLINED and only exposes cancel/self-approve to the
  // broker) because reject-to-draft is the ONLY path through which a
  // major field can be corrected without cancelling and rewriting the
  // policy.
  const handleRejectPolicyToDraft = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      title: 'Reject to Draft',
      children: (
        <RejectToDraft
          policy={policy}
          onProceed={() => {
            closeModal();
            onSuccess();
          }}
          onCancel={closeModal}
        />
      ),
      classes: '!p-0 r-[5px]',
      size: 'lg'
    });
  };

  const handleCancelPolicy = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      children: (
        <ReviewPolicy
          policy={policy}
          onProceed={() => {
            closeModal();
            onSuccess();
          }}
          onCancel={closeModal}
          title="Cancel Policy"
          titleIcon={<XIcon />}
          type={POLICY_REVIEW_STATES.CANCELLED}
        />
      ),
      classes: '!p-0 r-[5px]',
      size: 'lg'
    });
  };

  const handleRequestToCancelPolicy = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      children: (
        <ReviewPolicy
          policy={policy}
          onProceed={() => {
            closeModal();
            onSuccess();
          }}
          onCancel={closeModal}
          title="Request Policy Cancellation"
          titleIcon={<XIcon />}
          type={POLICY_REVIEW_STATES.CANCELLED}
          request
        />
      ),
      classes: '!p-0 r-[5px]',
      size: 'lg'
    });
  };

  const handleSubmitForApproval = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      children: (
        <SubmitForApproval
          policy={policy}
          onSubmitted={() => {
            closeModal();
            onSuccess();
          }}
          onCancel={closeModal}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  const handleSetPolicyOnHold = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      title: 'Put Policy On Hold',
      children: (
        <ReviewPolicy
          policy={policy}
          onProceed={() => {
            closeModal();
            onSuccess();
          }}
          onCancel={closeModal}
          title="Put Policy on Hold"
          titleIcon={<PauseIcon />}
          type={POLICY_REVIEW_STATES.ON_HOLD}
        />
      ),
      classes: '!p-0 r-[5px]',
      size: 'lg'
    });
  };

  const handleApprovePolicy = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      title: 'Approve Policy',
      children: (
        <PolicyApproval
          policy={policy}
          onApproved={() => {
            closeModal();
            onSuccess();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
          }}
          onCancel={closeModal}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  // Self Approval modal — same look as the standard approve modal, but
  // PolicyApproval is told to route through /self-approve instead of
  // /approve via the selfApprove flag. The backend rejects the call with
  // a 403 if the intermediary's issuance mode is not MID_LIVE / INSURER_API,
  // so this is safe even if a stale FE state surfaces the button.
  const handleSelfApprovePolicy = (policy: Policy | FleetPolicy, onSuccess: () => void) => {
    updateModalStates({
      show: true,
      title: 'Self Approval',
      children: (
        <PolicyApproval
          policy={policy}
          selfApprove
          onApproved={() => {
            closeModal();
            onSuccess();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
          }}
          onCancel={closeModal}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  const handleRouteToCreatePolicyView = (isRenewal?: string) => {
    try {
      dispatch(CLEAR_QUOTE_WIDGET_PAYLOAD());
      clearPolicyJourneyWizardData(isRenewal)
      const renewalParam = isRenewal === 'true' ? '&isRenewal=true' : '';
      navigate(`${getDashboardBase()}/policies/dynamic-create-policy?type=motor${renewalParam}`, { replace: true });

    } catch (error) {

    }
  };

  const assignPolicyToBroker = (policy: Policy | FleetPolicy) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AssignPolicyManager
          policy={policy}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
          }}
          onClose={closeModal}
          actionType='assignToBroker'
        />
      ),
      title: 'Assign Policy to Broker',
    });
  };

  const showPaymentLinkModal = (paymentLink: string, policy: NonAutoPolicy | undefined, paymentId?: number) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <PaymentLinkModal
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] })
          }}
          onClose={closeModal}
          link={paymentLink}
          paymentId={paymentId}
          phone={policy?.applicant?.phoneNumber}
        />
      ),
      title: 'Payment Link',
      showTitleSection: false,
      classes: '!p-0 r-[5px]'
    });
  };

  const showEditPaymentModal = (payment: any, policyId: string | undefined) => {
    const paymentId = payment?.id;
    if (paymentId != null && offlinePaymentInFlight.current.has(paymentId)) {
      // A previous offline-payment for this row is still being processed
      // (or its post-submit refetch hasn't landed yet). Refuse to open a
      // second modal so the user can't submit the same payment twice and
      // see the backend's "already processed" 400 as an error toast.
      showToast.info('This payment is already being processed. Please wait a moment.');
      return;
    }
    if (paymentId != null) offlinePaymentInFlight.current.add(paymentId);

    const releaseLock = () => {
      if (paymentId != null) offlinePaymentInFlight.current.delete(paymentId);
    };

    updateModalStates({
      show: true,
      showTitleSection: true,
      title: 'Process offline payment',
      size: 'xl',
      children: (
        <PaymentEditModal
          onProceed={() => {
            closeModal();
            // The list badge is derived from metaData.firstPaymentReceived
            // — processing an offline payment flips that server-side, so
            // the Motor Insurance Policies list must re-fetch or the row
            // stays on the pre-payment "Draft" badge until React Query's
            // stale timer fires. Mark it stale here so the next mount /
            // observer subscription pulls fresh data.
            queryClient.invalidateQueries({ queryKey: ['getPolicies'] });
            // Fleet policies flatten their vehicle-level payments into the
            // fleet detail's Payments table; processing one of those rows
            // routes through this same modal but leaves the enclosing
            // fleet page cached under a different key. Invalidate it too
            // so the fleet header/badge reflects the post-payment state
            // as soon as the operator navigates back.
            queryClient.invalidateQueries({ queryKey: ['getSingleFleetPolicy'] });
            if (policyId) {
              // Wait for the policy refetch to land before releasing the
              // lock — otherwise the dropdown could re-offer "Process
              // offline payment" before the row's status flips to SUCCESS.
              queryClient
                .refetchQueries({ queryKey: ['fetchPolicy'] })
                .finally(releaseLock);
            } else {
              releaseLock();
            }
          }}
          payment={payment}
          onClose={() => {
            releaseLock();
            closeModal();
          }}
        />
      ),
    });
  };

  const showPaymentApprovalModal = (payment: IPayment, onProceed: () => void) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <ApprovePaymentModal
          onSubmitted={() => {
            onProceed();
            closeModal();
          }}
          onCancel={closeModal}
          data={payment}
          title={'Approve Payment Edit'}
        />
      ),
      showTitleSection: false,
      classes: '!p-0 r-[5px]'
    });
  };

  const generatePaymentLink = (paymentId: string, onProceed: (response: any) => void) => {
    getPaymentLink(paymentId)
      .then((response) => {
        showToast.success(response?.message,);
        onProceed(response);
      })
      .catch((error: any) => {
        showToast.error(error.description ?? 'Failed to generate payment link. Please try again later.');
      })
      .finally(() => { })
  };

  const downloadFile = (fileLink: string) => {
    const link = document.createElement("a");
    link.href = `${fileLink}`; // your file URL
    link.download = `${fileLink}`; // suggested filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const paymentsColumns = (getPaymentActions: (payment: IPayment) => ITableAction[]) => [
    {
      Header: 'M',
      columns: [
        {
          Header: 'Application No.',
          accessor: 'applicationNumber',
          Cell: (row: any) => {
            return <span>{row.cell.value ?? '-'}</span>;
          }
        },
        {
          Header: 'Due Date',
          accessor: 'dueDate',
          Cell: (row: any) => {
            return (
              <span>
                {row.cell.value
                  ? getFormattedDate({ date: row.cell.value, showTime: false })
                  : '-'}
              </span>
            );
          }
        },
        {
          Header: 'Paid Date',
          accessor: 'dateOfPayment',
          Cell: (row: any) => {
            return (
              <span>
                {row.cell.value
                  ? getFormattedDate({ date: row.cell.value, showTime: false })
                  : '-'}
              </span>
            );
          }
        },
        {
          Header: 'Channel',
          accessor: 'channel',
          Cell: (row: any) => {
            return <span className="">{getFormattedPaymentChannel(row.cell.value)}</span>;
          }
        },
        {
          Header: 'Processor',
          accessor: 'processor',
          Cell: (row: any) => {
            return <span className="">{row.cell.value ?? '-'}</span>;
          }
        },
        {
          Header: 'Amount',
          accessor: 'preAmount',
          Cell: (row: any) => {
            return <span className="">{formatCurrencyAmount(row.cell.value) ?? '-'}</span>;
          }
        },
        {
          Header: 'Charged Amount',
          accessor: 'amount',
          Cell: (row: any) => {
            return <span className="">{formatCurrencyAmount(row.cell.value) ?? '-'}</span>;
          }
        },
        {
          Header: 'Processor Ref',
          accessor: 'reference',
          Cell: (row: any) => {
            return <span className="">{row.cell.value ?? '-'}</span>;
          }
        },
        {
          Header: 'Payment Receipt',
          accessor: 'evidenceUrl',
          Cell: (row: any) => {
            return <span className="">{row.cell.value ?
              <div className='flex gap-4 items-center'>
                <Link to={`${row.cell.value}`} target="_blank" >
                  <LuExternalLink className='h-5 w-5 cursor-pointer' />
                </Link>
                <Download onClick={() => downloadFile(row.cell.value)} className='h-5 w-5 cursor-pointer' /></div> : '-'}</span>;
          }
        },
        {
          id: 'Action',
          Header: 'Actions',
          Cell: (row: any) => {
            if (getPaymentActions(row.row.original)?.length > 0)
              return (
                <div className="cursor-pointer flex justify-center">
                  <PaymentStatus row={row} actions={getPaymentActions(row.row.original)} />
                </div>
              );
          }
        }
      ]
    }]

  const updatePaymentDetails = (policy: NonAutoPolicy | undefined) => {
    updateModalStates({
      show: true,
      children: (
        <CompleteNonAutoPolicyPayment
          policy={policy}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
          }}
          isUpdateModal={true}
          onCancel={closeModal} />
      ),
      title: 'Update Payment Details',
      classes: '!p-0 r-[5px]',
      size: 'lg'
    });
  }

  const handleGeneratePaymentReceipt = (policy: Policy) => {
    const totalPayments = policy?.payments?.length;
    const premium = policy?.quote?.paymentPlan?.totalPremium;
    const paid = policy?.totalPaid;
    const balance = policy?.balanceRemaining;
    const hasBalance = balance && balance > 0;

    updateModalStates({
      show: true,
      title: 'Generate Receipt',
      children: (
        <div className="space-y-6 p-4">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-darkBg-mainText">Generate Payment Receipt</h2>
            <p className="mt-1 text-sm text-gray-500">Review payment details before generating receipt</p>
          </div>

          {/* Payment Summary Card */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm dark:bg-darkBg dark:!text-darkBg-mainText dark:border-darkBg-border">
            <div className="px-5  text-center py-4 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-100">
              <h3 className="font-medium text-blue-800">Payment Summary</h3>
            </div>
            <div className="divide-y divide-gray-100 p-4  dark:divide-darkBg-border">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500">Policy Number</p>
                    <p className="text-sm font-medium text-gray-900 dark:!text-darkBg-mainText">{policy?.ref || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Customer Name</p>
                    <p className="text-sm font-medium text-gray-900 dark:!text-darkBg-mainText">{policy?.applicant?.fullName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Insurer</p>
                    <p className="text-sm font-medium text-gray-900 dark:!text-darkBg-mainText">{policy?.insurer?.name || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500">Total Payments</p>
                    <p className="text-sm font-medium text-gray-900 dark:!text-darkBg-mainText">{totalPayments || '0'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Premium Amount</p>
                    <p className="text-sm font-medium text-gray-900 dark:!text-darkBg-mainText">
                      {premium ? formatCurrencyAmount(premium) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Status */}
              <div className="bg-gray-50 mt-3 dark:bg-darkBg dark:!text-darkBg-mainText dark:border-darkBg-border">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-xs font-medium text-gray-500 dark:!text-darkBg-mainText">Paid</p>
                    <p className="text-base font-semibold text-green-600">
                      {formatCurrencyAmount(paid)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-medium text-gray-500 dark:!text-darkBg-mainText">Balance</p>
                    <p className={`text-base font-semibold ${hasBalance ? 'text-amber-600' : 'text-green-600'}`}>
                      {formatCurrencyAmount(balance)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-medium text-gray-500 dark:!text-darkBg-mainText">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${hasBalance ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                      }`}>
                      {hasBalance ? 'Not Fully Paid' : 'Fully Paid'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="w-full flex gap-4 mt-6 !mb-4">
            <Button
              type="button"
              label="Cancel"
              bgColor='secondary'
              onClick={closeModal}
              fullWidth
            />
            <Button
              type="button"
              bgColor='primary'
              label='Generate Receipt'
              onClick={async () => {
                if (!policyNumber) return;
                try {
                  const response = await generatePaymentReceipt(policyNumber);
                  if (response) {
                    closeModal();
                    showToast.success(response?.message ?? 'Payment receipt generated successfully',);
                    queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
                  }
                } catch (error: any) {
                  closeModal();
                  showToast.error(error.description ?? 'Failed to generate payment receipt',);
                }
              }}
              fullWidth
            />
          </div>
        </div>
      ),
      size: 'md',
    });
  };

  const assignPolicyManager = (policy: Policy) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AssignPolicyManager
          policy={policy}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
          }}
          onClose={closeModal}
          actionType='assignToManager'
        />
      ),
      title: 'Assign Policy Manager',
      classes: '!p-0 r-[5px]'
    });
  };

  const exportPolicyDetails = async (ref: string) => {
    try {
      const response: any = await downloadPolicyDetails(ref);
      window.open(response?.data?.docUrl, '_blank');
      closeModal();
      showToast.success(response.message ?? 'Successful request to download policy data.',);
    } catch (error: any) {
      closeModal();
      showToast.error(error.description ?? 'Failed to download policy data.',);
    }
  };

  const showDownloadPolicyDetailsModal = (resourceName: string) => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ActionConfirmation
          action="download"
          type='info'
          resourceType={'policy'}
          resourceName={resourceName}
          onClose={closeModal}
          onConfirm={() => exportPolicyDetails(resourceName)}
        />
      ),
      title: 'Download Policy Details',
      classes: '!p-0 r-[5px]'
    });
  };

  const handleDeletePolicyByNumber = (policyId: string) => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ActionConfirmation
          type="danger"
          onClose={closeModal}
          action="delete this policy"
          onConfirm={async () => {
            try {
              const response = isMotor ? await deleteMotorPolicy(policyId) : await deleteSingleNonMotorPolicy(policyId);
              if (response) {
                showToast.success(response?.message ?? 'Policy deleted successfully',);
                queryClient.invalidateQueries({ queryKey: ['fetchPolicies'] });
                closeModal();
                policyNumber && navigate(`${getDashboardBase()}/policies?type=non-motor`)
              }
            } catch (error: any) {
              showToast.error(error.description ?? 'Failed to delete policy. Try again');
            } finally {
              closeModal();
            }
          }}
          confirmationMessage="Are you sure you want to delete this policy? This action removes this policy from our system forever and can not be undone."
        />
      ),
      title: 'Delete Policy',
    });
  }

  const getPolicyVehiclePhotos = (policyNumber: string, selectVehicleId: number) => {
    updateModalStates({
      show: true,
      size: 'sm',
      showTitleSection: true,
      children: (
        <ActionConfirmation
          type="success"
          onClose={closeModal}
          action="proceed"
          onConfirm={async () => {
            try {
              requestPolicyVehiclePhotos(policyNumber, selectVehicleId)
                .then((response: any) => {
                  showToast.success(response?.message ?? 'Policy vehicle  photos request successful',);
                })
                .catch((error: any) => {
                  showToast.error(error?.description ?? 'Could not request the vehicle photos. Please try again.');
                });
            } catch (error: any) {
              showToast.error(error?.description ?? 'Could not request the vehicle photos. Please try again.');
              queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
            } finally {
              closeModal();
            }
          }}
        />
      ),
      title: 'Request Vehicle Photos',
    });

  };

  const sendAgreementFormToCustomer = (policy: Policy, isPaymentAgreement: boolean) => {
    if (policy) {
      updateModalStates({
        show: true,
        size: 'sm',
        showTitleSection: true,
        children: (
          <ShareDocumentModal
            policy={policy}
            onClose={closeModal}
            onProceed={(url: string) => {
              closeModal();
              const documentName = url.split('type=')[1];
              updateModalStates({
                show: true,
                size: 'xl',
                children: (
                  <CopyLink
                    onClose={closeModal}
                    link={url}
                    title={`${removeUnderscoresAndDashes(documentName, true)} LINK`}
                  />
                ),

                classes: '!p-0 r-[5px]'
              });
            }}
            isPaymentAgreement={isPaymentAgreement}
          />
        ),

        title: isPaymentAgreement ? 'Share Payment Agreement' : 'Share Document',
        classes: '!p-0 r-[5px]'
      });
    }
  };

  const showMarkPolicyAsCompleteModal = (policy: Policy) => {
    updateModalStates({
      show: true,
      size: 'lg',
      children: (
        <MarkPolicyAsDone
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
          }}
          onClose={closeModal}
          policy={policy}
        />
      ),
      title: 'Mark Policy as Complete',
      classes: '!p-0 r-[5px]'
    });
  };

  // const completeAllPayments = (policyId: string, policy: Policy, setLoading:(bol:boolean)=> void) => {
  //   policyId &&
  //     makeBulkPaymentForPolicy(policyId)
  //       .then((response) => {
  //         const paymentLink = response?.data?.data;
  //         policy && policy?.applicant && showMotorPaymentLinkModal(paymentLink, policy?.applicant?.phoneNumber, undefined);
  //       })
  //       .catch(() => {
  //         showToast.error('Failed to generate payment link. Please try again later.');
  //       })
  //       .finally(() => setLoading(false));
  // };

  const showCreateTaskModal = (setRefresh: (bol: boolean) => void, policyId: string, task?: ITask, isEdit = false) => {
    updateModalStates({
      show: true,
      size: 'xl',
      showTitleSection: true,
      children: (
        <CreateTask
          onProceed={() => {
            setRefresh(true);
            closeModal();
          }}
          onClose={closeModal}
          id={policyId}
          isEdit={isEdit}
          task={task}
          type={'POLICY'}
        />
      ),
      title: isEdit ? 'Update Task' : 'Create Task',
      classes: '!p-0 r-[5px]'
    });
  };

  // const makePaymentForSelectedIds = async (selectedPayments: any, policy: Policy) => {
  //   try {
  //     if (selectedPayments) {
  //       const payload = {
  //         ...selectedPayments,
  //         paymentIds: selectedPayments
  //       };
  //       const response = await makeSelectedPaymentsForAPolicy(payload);
  //       const paymentLink = response?.data?.data;
  //       policy && policy?.applicant && showMotorPaymentLinkModal(paymentLink, policy?.applicant?.phoneNumber, undefined);
  //     }
  //   } catch (error: any) {
  //     showToast.error(error.description ?? 'Failed to generate payment link. Please try again later.');
  //   }
  // };

  const applyDiscount = (policy: Policy) => {
    updateModalStates({
      show: true,
      children: (
        <ApplyDiscount
          closeModal={closeModal}
          onProceed={() => {
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
            closeModal;
          }}
          policy={policy}
        />
      ),
      title: 'Apply Discount',
      classes: '!p-0 r-[5px]',
      size: 'sm'
    });
  };

  // const fetchPolicyAutoDebitStatus = async (policyId: string) => {
  //   try {
  //     if (policyId) {
  //       const response = await getPolicyAutoDebitStatus(policyId);
  //       if (response?.data) {
  //         setAutoDebitStatus(response?.data);
  //       }
  //     }
  //   } catch (error: any) {
  //     console.log("Error ", error)
  //   }
  // };

  // const generateWebsitePaymentLink = () => {
  //   const url = `${getClientWebsiteUrl()}/policies/${policy?.ref}/pay`;
  //   const shareData = { url: url };

  //   shareContennt(shareData);
  // };


  // const showCustomerPhoneVerificationModal = (phone: string) => {
  //   updateModalStates({
  //     show: true,
  //     size: 'md',
  //     children: (
  //       <CustomerPhoneVerificationModal
  //         policy={policy}
  //         close={closeModal}
  //         onProceed={() => {
  //           closeModal();
  //           queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
  //         }}
  //         phoneNumber={phone}
  //         id={Number(policy?.applicant?.id)}
  //       />
  //     ),

  //     classes: '!p-0 r-[5px]'
  //   });
  // };

  const assignNonMotorPolicyManager = (nonMotor: NonAutoPolicy) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AssignNonMotorPolicy
          nonMotor={nonMotor}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
          }}
          onClose={closeModal}
        />
      ),
      title: 'Assign Non-Motor Policy Manager',
      classes: '!p-0 r-[5px]'
    });
  };

  const handleEditVehicleInformationModal = (policy: Policy, title: string, isVehicleEdit?: boolean, isVehicleOwnerEdit?: boolean, isOtherInfoEdit?: boolean, isInsuranceInfoEdit?: boolean, isRefereeInfoEdit?: boolean, selectedVehicleNumber?: string) => {
    updateModalStates({
      show: true,
      static: true,
      title: `${title}`,
      size: '3xl',
      children: (
        <VehicleInformationForm
          onSubmit={(values: VehicleFormValues) => { }}
          policy={policy}
          onClose={closeModal}
          isVehicleEdit={isVehicleEdit}
          isVehicleOwnerEdit={isVehicleOwnerEdit}
          isOtherInfoEdit={isOtherInfoEdit}
          isInsuranceInfoEdit={isInsuranceInfoEdit}
          isRefereeInfoEdit={isRefereeInfoEdit}
          selectedVehicleNumber={selectedVehicleNumber}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const fetchNonMotorPolicy = async (policyId: string) => {
    try {
      if (policyId) {
        const response = await getNonMotorPolicyByPolicyNumber(policyId);
        return (new NonAutoPolicy(response));
      }
    } catch (error) {
      showToast.error('Failed to fetch policy details. Please try again later.');
    }
  };

  const openPictureModal = (imgURL: string) => {

    imgURL && updateModalStates({
      children: (
        <PreviewImage onClose={closeModal} imgURL={imgURL} />
      ),
      show: true,
      size: '3xl',
      hidePanelStyles: true,
      showTitleSection: false,
    });
  };

  const sendReviewLinkToCustomer = (policy: Policy) => {
    if (policy) {
      updateModalStates({
        show: true,
        size: 'sm',
        title: 'Request Policy Review',
        children: (
          <RequestReviewModal
            policy={policy}
            onClose={closeModal}
            onProceed={(url: string) => {
              closeModal();
              const documentName = url.split('type=')[1];
              updateModalStates({
                show: true,
                size: 'xl',
                children: (
                  <CopyLink
                    onClose={closeModal}
                    link={url}
                    title={`${removeUnderscoresAndDashes(documentName, true)} LINK`}
                  />
                ),
                title: 'Share Review Link',
                classes: '!p-0 r-[5px]'
              });
            }}
          />
        ),

        classes: '!p-0 r-[5px]'
      });
    }
  };

  const runPaymentAnalysis = async (policy: Policy,) => {
    try {
      const response = await runPaymentAnalysisOnPolicy(Number(policy?.ref))
      if (response) {
        showToast.success(response.message);
      }
      queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] })

    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to run payment analysis. Please try again later.');
    }
  }

  const showMotorPaymentLinkModal = (paymentLink: string, phone: string | undefined, paymentId?: number) => {
    // Defence in depth: never open the Payment Link modal with an empty
    // link. Callers should already guard against this, but a missed
    // null-check anywhere upstream used to result in a popup with a
    // blank URL field and dead Email/SMS/WhatsApp buttons (see image 19
    // from the EMI-completion flow).
    const trimmed = typeof paymentLink === 'string' ? paymentLink.trim() : '';
    if (!trimmed) {
      showToast.error('Payment link could not be generated. Please try again or contact support.');
      return;
    }
    updateModalStates({
      show: true,
      size: 'md',
      showDescription: false,
      children: (
        <PaymentLinkModal
          onProceed={() => {
            closeModal();
          }}
          onClose={closeModal}
          link={trimmed}
          paymentId={paymentId}
          phone={phone}

        />
      ),

      title: 'Payment Link',
      classes: '!p-0 r-[5px]'
    });
  };

  const exportFilteredPolicies = (
    reviewStage: string,
    searchParam?: string,
    expiredFrom?: string,
    expiredTo?: string,
    createFrom?: string,
    createTo?: string,
    startFrom?: string,
    startTo?: string,
    coverType?: string,
    vehicleUsage?: string,
    leadSource?: string,
    managerId?: string,
    insurerId?: number,
    branchId?: number,) => {

    const data: any = {
      reviewStage,
      searchParam,
      expiredFrom: expiredFrom ? moment(expiredFrom).format(INPUT_DATE_FORMAT) : undefined,
      expiredTo: expiredTo ? moment(expiredTo).format(INPUT_DATE_FORMAT) : undefined,
      createFrom: createFrom ? moment(createFrom).format(INPUT_DATE_FORMAT) : undefined,
      createTo: createTo ? moment(createTo).format(INPUT_DATE_FORMAT) : undefined,
      startFrom: startFrom ? moment(startFrom).format(INPUT_DATE_FORMAT) : undefined,
      startTo: startTo ? moment(startTo).format(INPUT_DATE_FORMAT) : undefined,
      coverType,
      vehicleUsage,
      leadSource,
      managerId,
      insurerId,
      branchId
    };

    downloadPolicies(data)
      .then((response: any) => {
        window.open(response.documentUrl, '_blank');
      })
      .catch((response: any) => {
        showToast.error(response?.description);
      })
      .finally(() => { });
  };

  const provideMomoDetails = (policy: Policy) => {
    updateModalStates({
      show: true,
      children: (
        <ProvideMomoDetails
          policy={policy}
          onProceed={(data: { phone: string; network: MOBILE_MONEY_NETWORKS }) => {
            initializeHubtel({ policy: policy, ...data }).then(
              (response: IVerifyMobilePaymentDTO) => {
                closeModal();
                confirmPaymentOTP(response, data.phone);
              }
            );
          }}
        />
      )
    });
  };

  const confirmPaymentOTP = (payload: IVerifyMobilePaymentDTO, phone: string) => {
    updateModalStates({
      show: true,
      children: (
        <VerifyPaymentOTP
          data={payload}
          phone={phone}
          onProceed={() => {
            showToast.success('Recurring payment initiated successfully for this policy.');
            closeModal();
          }}
        />
      )
    });
  };

  const fleetColumns = useMemo(
    () => [
      {
        name: 'Fleet ID',
        selector: (row: any) => row.fleetNumber,
        sortable: true,
        grow: 2,
        cell: (row: any) => (
          <Link
            to={`${getDashboardBase()}/policies/fleets/${row.fleetNumber}`}
          >
            <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.fleetNumber ?? '-'}</span>
          </Link>
        )
      },
      {
        name: 'Actions',
        cell: (row: any) => {
          const baseUrl = window.location.origin;
          return (
            <>
              <IconAction
                messageIconOne={`Copy fleet ID`}
                IconOne={TbClipboardCopy}
                onclickIconOne={async () => {
                  await navigator.clipboard.writeText(`${row.fleetNumber}`);
                }}

                messageIconTwo={`Copy fleet link`}
                IconTwo={FaLink}
                onclickIconTwo={async () => {
                  await navigator.clipboard.writeText(
                    baseUrl +
                    '/admin' +
                    `policies/fleets/${row.fleetNumber}`
                  );
                }}

                messageIconThree={`Open fleet`}
                IconThree={LuExternalLink}
                linkTo={`${getDashboardBase()}/policies/fleets/${row.fleetNumber}`}
              />
            </>
          );
        },
        ignoreRowClick: true,
        width: '120px',
      },
      {
        name: 'Customer',
        selector: (row: any) => row.customer?.name,
        sortable: true,
        grow: 2,
        cell: (row: any) => (
          <Link
            to={`${getDashboardBase()}/customers/${row.customer?.id}`}
            className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink" target="_blank">
            {row.customer?.name ? row.customer.name : '-'}
          </Link>
        )
      },
      {
        name: 'Phone #',
        selector: (row: any) => row.customer?.phone,
        sortable: true,
        cell: (row: any) => <span>{getPhoneNumberFromShortCodeFormat(row.customer?.phone) ?? '-'}</span>
      },
      // {
      //   name: 'Status',
      //   selector: (row: any) => row.active,
      //   sortable: true,
      //   cell: (row: any) => (
      //     <div className="">
      //       {row.active ? (
      //         <div className="px-2 border  border-[#0F7125] text-xs flex  h-[24px] w-fit items-center justify-center text-[#0F7125] rounded-full ">
      //           <CheckCircle className="inline-block w-4 h-4 mr-1" />Active
      //         </div>
      //       ) : (
      //         <div className=" px-2 border border-red-800 text-xs flex  h-[24px] w-fit items-center justify-center text-red-800 rounded-full ">
      //           <Ban className="inline-block w-4 h-4 mr-1" /><span>Inactive</span>
      //         </div>
      //       )}
      //     </div>
      //   )
      // },
      {
        name: 'Total vehicles',
        selector: (row: any) => row.currentFleetSize,
        sortable: true,
        cell: (row: any) => <span>{row.currentFleetSize ? row.currentFleetSize : '-'}</span>
      },
      {
        name: 'Contract Status',
        selector: (row: any) => row.contractStatus?.displayName,
        sortable: true,
        cell: (row: any) => <span>{row.contractStatus?.displayName ? row.contractStatus.displayName : '-'}</span>
      },
      // {
      //   name: 'Installment Type',
      //   selector: (row: any) => row.installmentPeriod,
      //   sortable: true,
      //   cell: (row: any) => (
      //     row.installmentPeriod ? <PolicyStatus
      //       status={getInstallmentTypeFromKey(row.installmentPeriod)}
      //       classes="px-2"
      //     /> : <span>-</span>
      //   )
      // },
      {
        name: 'Premium',
        selector: (row: any) => row.paymentPlan?.outrightPremium,
        sortable: true,
        cell: (row: any) => (
          <div className="min-w-[100px]">
            <span>{formatCurrencyAmount(row.paymentPlan?.outrightPremium, row.currency || 'GHS')}</span>{' '}
          </div>
        )
      },
      // {
      //   name: 'Initial Deposit',
      //   selector: (row: any) => row.paymentPlan?.initialDeposit,
      //   sortable: true,
      //   cell: (row: any) => (
      //     <div className="min-w-[100px]">
      //       <span> {formatCurrencyAmount(row.paymentPlan?.initialDeposit, row.currency || 'GHS')}</span>
      //     </div>
      //   )
      // },
      {
        name: 'Inception Date',
        selector: (row: any) => row.startDate,
        sortable: true,
        cell: (row: any) => (
          <span>
            {row.startDate ? moment(row.startDate).format('DD/MM/YY h:mm a') : '-'}
          </span>
        )
      },
      {
        name: 'Insurer',
        selector: (row: any) => row.insurerinfo?.name,
        sortable: true,
        cell: (row: any) => <span>{row.insurerinfo?.name ? row.insurerinfo.name : '-'}</span>
      },
      {
        name: 'Owner',
        selector: (row: any) => row.brokerInfo?.name,
        sortable: true,
        cell: (row: any) => <span>{row.brokerInfo?.name ? row.brokerInfo.name : '-'}</span>
      },
      {
        name: 'Manager',
        selector: (row: any) => row.managerName,
        sortable: true,
        cell: (row: any) => (
          <span>
            {row.managerName ? row.managerName : '-'}
          </span>
        )
      },
      {
        name: 'Created On',
        selector: (row: any) => row.createdAt,
        sortable: true,
        cell: (row: any) => (
          <span>
            {row.createdAt ? moment(row.createdAt).format('DD/MM/YY h:mm a') : '-'}
          </span>
        )
      }
    ],
    [countryId]
  );

  const homeColumns = useMemo(
    () => [
      {
        name: 'Customer',
        selector: (row: any) => row.applicant?.fullName,
        sortable: true,
        cell: (row: any) => <span>{row.applicant?.fullName ?? '-'}</span>
      },
      {
        name: 'Phone #',
        selector: (row: any) => row.applicant?.phoneNumber,
        sortable: true,
        cell: (row: any) => <span>{getPhoneNumberFromShortCodeFormat(row.applicant?.phoneNumber) ?? '-'}</span>
      },
      {
        name: 'Policy ID',
        selector: (row: any) => row.ref,
        sortable: true,
        cell: (row: any) => <span className="">{row.ref ?? '-'}</span>
      },
      // {
      //   name: 'Installment Type',
      //   selector: (row: any) => row.quote?.paymentPlan?.installmentPeriod,
      //   sortable: true,
      //   cell: (row: any) => (
      //     <span>
      //       {row.quote?.paymentPlan?.installmentPeriod ? <>{getInstallmentTypeFromKey(row.quote.paymentPlan.installmentPeriod)}</> : '-'}
      //     </span>
      //   )
      // },
      {
        name: 'Premium',
        selector: (row: any) => row.quote?.paymentPlan?.outRightPremium,
        sortable: true,
        cell: (row: any) => (
          <div className="min-w-[100px]">
            <span>{formatCurrencyAmount(row.quote?.paymentPlan?.outRightPremium, row.currency || 'GHS')}</span>{' '}
          </div>
        )
      },
      // {
      //   name: 'Initial Deposit',
      //   selector: (row: any) => row.quote?.paymentPlan?.initialDeposit,
      //   sortable: true,
      //   cell: (row: any) => (
      //     <div className="min-w-[100px]">
      //       <span>{formatCurrencyAmount(row.quote?.paymentPlan?.initialDeposit, row.currency || 'GHS')}</span>
      //     </div>
      //   )
      // },
      {
        name: 'Inception Date',
        selector: (row: any) => row.startsOn,
        sortable: true,
        cell: (row: any) => (
          <span>
            {row.startsOn ? moment(row.startsOn).format('DD/MM/YY h:mm a') : '-'}
          </span>
        )
      },
      {
        name: 'Created On',
        selector: (row: any) => row.createdOn,
        sortable: true,
        cell: (row: any) => (
          <span>
            {row.createdOn ? moment(row.createdOn).format('DD/MM/YY h:mm a') : '-'}
          </span>
        )
      }
    ],
    []
  );

  const archiveAPolicy = async (policy: Policy, isFromSinglePolicy?: boolean) => {
    const policyNumber = policy.ref;

    updateModalStates({
      show: true,
      title: 'Archive Policy',
      showCloseButton: false,
      size: 'sm',
      children: (
        <ActionConfirmation
          action="archive this policy"
          type='warning'
          confirmationMessage='Are you sure you want to archive this policy? This action removes this policy from your policies list.'
          onClose={closeModal}
          onConfirm={() => {
            archivePolicy(policyNumber, true)
              .then((response: any) => {
                if (response) {
                  closeModal();
                  showToast.success(response?.message);
                  isFromSinglePolicy ? navigate(`${getDashboardBase()}/policies?type=motor`) : queryClient.invalidateQueries({ queryKey: ['getPolicies'] });
                }
              })
              .catch((error: any) => {
                closeModal();
                showToast.error(error?.description ?? 'Could not delete the policy. Please try again.');
              });
          }}
        />
      )
    });
  };

  const handleGeneratePolicyDocuments = async (policy: Policy) => {
    if (policy) {
      try {
        const response = await generatePolicyAgreementDocuments(policy?.ref);
        if (response) {
          showToast.success(response?.message ?? 'Policy documents generated successfully');
          queryClient.invalidateQueries({ queryKey: ['fetchPolicy', policy?.ref] });
        }
      } catch (error: any) {
        showToast.error(error?.description ?? 'Failed to generate policy documents');
      }
    }
  }

  const handleGenerateSaleForAPolicy = async (policy: Policy) => {
    if (policy) {
      try {
        const response = await generateSaleForAPolicy(policy?.ref);
        if (response) {
          showToast.success(response?.message ?? 'Sale generated successfully');
          queryClient.invalidateQueries({ queryKey: ['fetchPolicy', policy?.ref] });
        }
      } catch (error: any) {
        showToast.error(error?.description ?? 'Failed to generate policy documents');
      }
    }

  }

  const showGenerateStickerForm = (policy: Policy, onProceed: () => void) => {

    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <GenerateStickerModal
          policy={policy}
          onProceed={() => {
            closeModal();
            onProceed();
          }}
          onClose={closeModal}
        />
      ),
      title: 'Generate Insurance Sticker',
      showCloseButton: true,
      classes: '!p-0 r-[5px]'
    });
  };

  const manuallyVerifyVIN = async (policy: Policy) => {
    const vehicleID = Number(policy?.vehicle?.id);
    try {
      if (vehicleID) {
        const response = await verifyVehicle(vehicleID);
        if (response) {
          window.location.reload();
          showToast.success(response.message);
        }
      }
    } catch (error: any) {
      showToast.error(error?.description ?? 'Could not verify the vehicle. Please try again.');
    }
  };

  const completeAllPayments = (policyId: string, policy: Policy, setLoading: (bol: boolean) => void) => {
    policyId &&
      makeBulkPaymentForPolicy(policyId)
        .then((response) => {
          // Some backends 200 with an empty/null payload when generation
          // silently fails (e.g. amount over gateway limit). Don't open
          // the Payment Link modal in that case — an empty link with
          // dead Email/SMS/WhatsApp buttons is worse UX than a clear
          // toast. Trim because some upstreams return a single space.
          const paymentLink = response?.data?.data;
          const linkStr = typeof paymentLink === 'string' ? paymentLink.trim() : '';
          if (!linkStr) {
            showToast.error(
              response?.data?.message
              || 'Payment link could not be generated. The combined amount may exceed the gateway limit — try paying one installment at a time, or contact support.'
            );
            return;
          }
          if (policy?.applicant) {
            showMotorPaymentLinkModal(linkStr, policy.applicant.phoneNumber, undefined);
          }
        })
        .catch((error: any) => {
          // Preserve the backend message when one comes back; otherwise
          // a sensible default.
          const description = error?.description || error?.message;
          showToast.error(description || 'Failed to generate payment link. Please try again later.');
        })
        .finally(() => setLoading(false));
  };


  const { data: policiesCount, isFetching: isFetchingPoliciesCount } = useQuery({
    queryKey: ['getAllBrokerPoliciesCount', countryId],
    queryFn: async () => {
      const response: any = await getAllBrokerPoliciesCount(countryId);
      return response;
    },
  });

  return {
    archiveAPolicy,
    confirmPaymentOTP,
    provideMomoDetails,
    fetchPolicy,
    exportFilteredPolicies,
    requestPolicy,
    getAllPolicies,
    approveSinglePolicy,
    selfApproveSinglePolicy,
    updatePolicyDetails,
    reviewPolicy,
    cancelPolicy,
    generatePolicyFromQuote,
    POLICY_FILTERS,
    INSURER_POLICY_FILTERS,
    renewMotorPolicy,
    addQuote,
    assignPoliciesToCommunity,
    setPolicyToDueLater,
    handleReloadPolicyVehiclePhotos,
    uploadFleet,
    handleApplyLoanToPolicy,
    PolicyTypesBadge,
    POLICY_TYPES,
    getAllExpiringPolicies,
    handleQuoteCalculator,
    handleApprovePolicy,
    handleSelfApprovePolicy,
    handleSubmitForReview,
    handleSubmitForApproval,
    handleDeclinePolicy,
    handleRejectPolicyToDraft,
    handleCancelPolicy,
    handleRequestToCancelPolicy,
    handleSetPolicyOnHold,
    handleRouteToCreatePolicyView,
    uploadPolicyDocument,
    assignPolicyToBroker,
    showPaymentLinkModal,
    showEditPaymentModal,
    showPaymentApprovalModal,
    generatePaymentLink,
    paymentsColumns,
    updatePaymentDetails,
    markAsRenewed,
    clearPolicyJourneyWizardData,
    handleGeneratePaymentReceipt,
    assignPolicyManager,
    showDownloadPolicyDetailsModal,
    assignNonMotorPolicyManager,
    getPolicyVehiclePhotos,
    sendAgreementFormToCustomer,
    showMarkPolicyAsCompleteModal,
    showCreateTaskModal,
    applyDiscount,
    PolicyModalComponent,
    handleDeletePolicyByNumber,
    handleEditVehicleInformationModal,
    fetchNonMotorPolicy,
    openPictureModal,
    sendReviewLinkToCustomer,
    runPaymentAnalysis,
    showMotorPaymentLinkModal,
    PolicyStatusBadge,
    fleetColumns,
    homeColumns,
    policiesCount,
    isFetchingPoliciesCount,
    handleGeneratePolicyDocuments,
    handleGenerateSaleForAPolicy,
    showGenerateStickerForm,
    manuallyVerifyVIN,
    completeAllPayments
  };
}

export default usePolicy;

export function useHubtelPayment() {
  const initializeHubtel = (data: {
    policy: Policy;
    phone: string;
    network: MOBILE_MONEY_NETWORKS;
  }): Promise<IVerifyMobilePaymentDTO> => {
    const payload: IInitiateHubtelPaymentDTO = {
      initialAmount: data.policy?.discountedAmount ?? 0,
      accountNumber: data.phone,
      network: data.network,
      policyNumber: data.policy?.ref ?? ''
    };

    return new Promise<IVerifyMobilePaymentDTO>((resolve, reject) => {
      initializeHubtelPayment(payload)
        .then((response: any) => {
          const payload: IVerifyMobilePaymentDTO = {
            otpCode: response.data.otpPrefix,
            policyNumber: data.policy?.ref ?? '',
            recurringInvoiceId: response.data.recurringInvoiceId,
            requestId: response.data.requestId
          };
          resolve(payload);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };

  return {
    initializeHubtel
  };
}

export function useItConsortiumPayment() {
  const initializeItConsortium = (data: {
    policy: Policy;
    phone: string;
    network: MOBILE_MONEY_NETWORKS;
  }): Promise<IVerifyMobilePaymentDTO> => {
    const payload: any = {
      accountNumber: data.phone,
      policyNumber: data.policy?.ref ?? ''
    };

    return new Promise<IVerifyMobilePaymentDTO>((resolve, reject) => {
      initializeItConsortiumPayment(payload.accountNumber, payload.policyNumber)
        .then((response: any) => {
          const payload: IVerifyMobilePaymentDTO = {
            otpCode: response.data.otpPrefix,
            policyNumber: data.policy?.ref ?? '',
            recurringInvoiceId: response.data.recurringInvoiceId,
            requestId: response.data.requestId
          };
          resolve(payload);
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  };

  return {
    initializeItConsortium,
  };
}